import React, { createContext, useContext, useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const CartContext = createContext();

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("lbbooks_cart")) || [];
        } catch {
            return [];
        }
    });
    const [isOpen, setIsOpen] = useState(false);
    const [shippingSettings, setShippingSettings] = useState(null);

    useEffect(() => {
        async function fetchSettings() {
            try {
                const snap = await getDoc(doc(db, "siteConfig", "shipping"));
                if (snap.exists()) setShippingSettings(snap.data());
            } catch (e) {
                console.error("Error fetching shipping settings:", e);
            }
        }
        fetchSettings();
    }, []);

    useEffect(() => {
        localStorage.setItem("lbbooks_cart", JSON.stringify(items));
    }, [items]);

    /**
     * Add an item to the cart.
     * item should have:
     *   - productId (the Firestore doc ID)
     *   - selectedFormat (e.g. "Paperback", "eBook")
     *   - price, title, isDigital, imageUrl, language, etc.
     *   - bundleItems (array) if format is "Bundle"
     *   - epubStoragePath if isDigital
     *
     * Cart key = `${productId}_${selectedFormat}` so the same book
     * in different formats can exist as separate line items.
     */
    function addItem(book) {
        // Build the cart-unique key
        const cartId = book.cartId
            || (book.selectedFormat ? `${book.productId || book.id}_${book.selectedFormat}` : book.id);

        setItems(prev => {
            const existing = prev.find(i => i.cartId === cartId);
            if (existing) {
                return prev.map(i =>
                    i.cartId === cartId ? { ...i, quantity: i.quantity + 1 } : i
                );
            }
            return [...prev, { ...book, cartId, quantity: 1 }];
        });
        setIsOpen(true);

        // Add Meta Pixel AddToCart event tracking
        if (window.fbq) {
            try {
                let cleanPrice = 0;
                if (book.price !== undefined && book.price !== null) {
                    const priceStr = String(book.price).replace(/[^\d.]/g, '');
                    cleanPrice = parseFloat(priceStr) || 0;
                }
                const contentId = book.metaContentId || book.productId || book.id || '';
                
                window.fbq('track', 'AddToCart', {
                    content_ids: [String(contentId).trim()],
                    content_type: 'product',
                    value: cleanPrice > 0 ? cleanPrice : 0.01,
                    currency: 'USD'
                });
            } catch (e) {
                console.error("Meta Pixel AddToCart tracking failed:", e);
            }
        }
    }

    function removeItem(cartId) {
        setItems(prev => prev.filter(i => (i.cartId || i.id) !== cartId));
    }

    function updateQuantity(cartId, qty) {
        if (qty <= 0) return removeItem(cartId);
        setItems(prev =>
            prev.map(i => (i.cartId || i.id) === cartId ? { ...i, quantity: qty } : i)
        );
    }

    function clearCart() {
        setItems([]);
    }

    const totalItems = items.reduce((s, i) => s + i.quantity, 0);

    return (
        <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, isOpen, setIsOpen, totalItems, shippingSettings }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    return useContext(CartContext);
}